import React, { useMemo } from 'react';
import { buildCountdownVideoStories } from 'fym-shared/src/music24/story/countdownVideo';
import type { AssetStyleId } from 'fym-shared/src/music24/assetStyles';
import type { ArtworkMotionShape } from 'fym-shared/src/music24/story/artworkMotionVideo';
import { isReleasedOn, storyPillText } from 'fym-shared/src/music24/story/storyCopy';
import type { KitShape } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../common/types';
import { countdownDays } from '../studioRules';
import type { StoryPreviewContext } from '../details/imageTiles/storyPreviewContext';
import type { FrameLyric } from './assetFrameVars';
import { CanvasVideoPreview } from './CanvasVideoPreview';
import { LyricMomentPreview } from './LyricMomentPreview';
import { MotionVideoPreview } from './MotionVideoPreview';
import { StoryPreview } from './StoryPreview';
import { studioStoryStyle } from './studioStory';

/** The short videos that code lays out and that step 3 draws with the tables of the render. */
export type ShortPreviewAssetType = 'countdown' | 'artworkInMotion' | 'canvasLoop' | 'lyricMoment';

/** True for an asset type whose step-3 preview is a frame of the short video (`ShortVideoPreview`). */
export function isShortPreviewType(assetTypeId: string): assetTypeId is ShortPreviewAssetType {
  return (
    assetTypeId === 'countdown' ||
    assetTypeId === 'artworkInMotion' ||
    assetTypeId === 'canvasLoop' ||
    assetTypeId === 'lyricMoment'
  );
}

/** The shape of the artwork in motion of a Studio shape pick (9:16 unless 4:5 or 1:1). */
function motionShapeOf(shape: KitShape | undefined): ArtworkMotionShape {
  return shape === '4:5' || shape === '1:1' ? shape : '9:16';
}

interface ShortVideoPreviewProps {
  assetTypeId: ShortPreviewAssetType;
  context: StoryPreviewContext;
  style: AssetStyleId | null;
  /** The shape pick of the artwork in motion. */
  shape?: KitShape;
  /** The picked lines of a lyric moment (the sung line and the next one). */
  lyric?: FrameLyric;
  width: number;
}

/**
 * The step-3 preview of a short video that code lays out (designs `asset-redesign/o`, `q`, `s`):
 * one frame, drawn with the layout of the render (fym-shared `storyLayout`, format `video916`). The
 * countdown shows its poster: the full still of act 1 for the first day still to come. The artwork
 * in motion shows a frame with the CTA in (its pill follows the date and the pre-save page).
 */
const ShortVideoPreviewComponent: React.FC<ShortVideoPreviewProps> = ({
  assetTypeId,
  context,
  style,
  shape,
  lyric,
  width,
}) => {
  const story = useMemo(
    () =>
      buildCountdownVideoStories({
        days: countdownDays(context.daysToRelease)[0] ?? 7,
        title: context.title,
        artist: context.artist,
        releaseDate: context.releaseDate,
        today: context.today,
        link: context.link,
        presave: context.presave,
        withLogo: context.logoUrl !== null,
        locale: context.locale,
      }).count,
    [context]
  );
  if (assetTypeId === 'lyricMoment') {
    return <LyricMomentPreview context={context} lyric={lyric} width={width} />;
  }
  if (assetTypeId === 'canvasLoop') {
    return <CanvasVideoPreview context={context} width={width} />;
  }
  if (assetTypeId === 'artworkInMotion') {
    const moment = isReleasedOn(context.releaseDate, context.today) ? 'outNow' : 'preSave';
    return (
      <MotionVideoPreview
        context={context}
        shape={motionShapeOf(shape)}
        particlesStyle={style === 'particles'}
        cta={storyPillText(moment, context.releaseDate, context.today, context.presave, context.locale)}
        width={width}
      />
    );
  }
  return (
    <StoryPreview
      story={story}
      look={context.look}
      palette={context.palette}
      // Owner decision 2026-10-08: the countdown always shows the cover.
      storyStyle={studioStoryStyle(style === 'typeOnly' ? 'coverCard' : style)}
      artworkUrl={context.artworkUrl}
      logoUrl={context.logoUrl}
      watermark={context.watermark}
      width={width}
    />
  );
};

export const ShortVideoPreview = TypedMemo(ShortVideoPreviewComponent);
